import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import LazyLoad from 'react-lazyload';
import { AppNoInfo, AppRemixIcon } from '@/layout/AppComponents';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath, path } from '../../../routing/helpers/router';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import Translator from '../../../layout/components/helpers/Translator';
import RiskManagerSidebar from '../../../layout/screenSpecifics/Modules/InecoRiskManager/Sidebar/InecoRiskManagerSidebar';
import InecoRiskManagerPaginator from '../../../layout/screenSpecifics/Modules/InecoRiskManager/InecoRiskManagerPaginator';
import ElementContainer from '../../../layout/screenSpecifics/Modules/InecoRiskManager/ElementContainer';
import ModuleInecoRiskManagerBase from './ModuleInecoRiskManagerBase';

export default function ModuleInecoRiskManagerRiskListScreen() {
  const { moduleBridgeHex, filtersHex, page } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.moduleData
  );
  const risksPage = useSelector((state) => state.currentPage?.risksPage);
  const riskLevel1 = useSelector(
    (state) => state.currentPage?.elementLevels?.level1
  );
  const [loading, setLoading] = useState(true);
  const [risks, setRisks] = useState(null);

  // > LOAD RISKS
  const loadRisks = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_inecoRiskManager_risk_list'), {
          moduleBridgeHex,
          filtersHex,
        })
        .then(({ data }) => {
          if (isMounted.current) {
            setRisks(data);
            dispatch(currentPageAction.change('pagination', data.pagination));
            dispatch(
              currentPageAction.change('risksPage', data.elementIDs, true)
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, filtersHex, page]
  );

  useEffect(() => {
    setLoading(true);
    loadRisks();
  }, [filtersHex]);
  // < LOAD RISKS

  // > LOAD RISK LEVELS
  const loadRiskLevel = async (level) =>
    axios.post(axiosPath('api_r_user_module_inecoRiskManager_risk_level'), {
      moduleBridgeHex,
      elementIDs: risks?.elementIDs?.[page - 1],
      level,
    });

  // Level 1
  useEffect(() => {
    if (risks?.elementIDs) {
      setLoading(false);
      window.scrollTo(0, 0);
      if (risks?.elementIDs.length > 0) {
        loadRiskLevel(1).then((riskLvl1) => {
          dispatch(
            currentPageAction.change(
              'elementLevels',
              {
                level1: !riskLevel1
                  ? riskLvl1?.data
                  : { ...riskLevel1, ...riskLvl1?.data },
              },
              true
            )
          );
        });
      }
    }
  }, [risks, page]);
  // < LOAD RISK LEVELS

  return (
    <>
      <ModuleInecoRiskManagerBase />
      <RiskManagerSidebar activeRoute="user_module_inecoRiskManager_risk_list" />
      <BreadcrumbHandler
        paths={
          moduleData
            ? [{ name: `${moduleData?.moduleName}` }]
            : [{ name: '...' }]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          {filtersHex !== '0' && (
            <div className="alert alert-info pointer">
              <span
                onClick={() =>
                  navigate(
                    path({
                      name: 'user_module_inecoRiskManager_risk_list',
                      params: {
                        moduleBridgeHex,
                        page: 1,
                        filtersHex: '0',
                      },
                    })
                  )
                }
              >
                <AppRemixIcon icon="delete-bin-6" className="mr-2" />
                <Translator file="filter" name="filter.delete" />
              </span>
              {/* <span
                className="ml-4"
                onClick={() =>
                  dispatch(
                    currentPageAction.change('auxSidebar', {
                      opened: 'filters',
                    })
                  )
                }
              >
                <AppRemixIcon icon="equalizer" className="mr-2" />
                <Translator file="module-legalCompliance" name="show.filters" />
              </span> */}
            </div>
          )}
          {loading ? (
            <AppNoInfo minHeight={window.innerHeight - 60} />
          ) : risksPage && risksPage.length > 0 ? (
            risksPage?.[page - 1].map((rID, i) => (
              <LazyLoad key={i} height={113}>
                <ElementContainer elementID={rID} type={moduleData?.type} />
              </LazyLoad>
            ))
          ) : (
            <div className="alert alert-info">Aún no existe ningún riesgo</div>
          )}
        </div>

        {risksPage?.length > 0 && risks && (
          <InecoRiskManagerPaginator
            pagination={risks?.pagination}
            route="user_module_inecoRiskManager_risk_list"
            page={page}
            plantObligationPages={risksPage}
            itemsPerPage={20}
            className="mt-3"
            params={{
              moduleBridgeHex,
              page,
              filtersHex,
            }}
          />
        )}
      </main>
    </>
  );
}
